import React from 'react';

import { useMutation, useApolloClient } from '@apollo/client';
import { MUTATION } from '../gql-operations';

import { AuthContext } from '../util/context';
import { CacheManager } from '../util/cacheManager';
import customAnalytics from '../util/custom-analytics';

/**
 * Hook for permanently deleting messages from trash
 * Uses centralized CacheManager for cache updates
 */
function useDeleteMessagesMutation() {
    const client = useApolloClient();
    const [deleteMessages, { data: successResponse }] = useMutation(MUTATION.DELETE_MESSAGES, {
        onCompleted: res => {
            // analytics on the bottom
        }
    });

    const {
        state: {
            folderFilterType,
            selectedFolder,
            showDeletedMessages,
            searchTerm,
            searchGlobal,
            searchDeletedMessages,
            searchFolderFilterType,
            searchActive,
            selectedMessage
        },
        context: { setMessagesToSend, setSelectedMessagesMap, setSelectedMessage }
    } = React.useContext(AuthContext);
    const normalizedSearchTerm = String(searchTerm || '').trim();

    const resolveMessageIdFromCache = React.useCallback((clientRequestId) => {
        const normalizedClientRequestId = String(clientRequestId || '').trim();
        if (!normalizedClientRequestId) {
            return '';
        }

        const cacheData = client.cache.extract();
        for (const entry of Object.values(cacheData)) {
            if (!entry || typeof entry !== 'object') {
                continue;
            }

            if (entry.__typename !== 'Message') {
                continue;
            }

            if (String(entry.clientRequestId || '').trim() !== normalizedClientRequestId) {
                continue;
            }

            return String(entry.id || '').trim();
        }

        return '';
    }, [client]);

    const resolveCanonicalMessageId = React.useCallback((message) => {
        const resolvedServerId = String(
            message?._resolvedServerId || message?.rawMessage?.id || ''
        ).trim();
        if (resolvedServerId) {
            return resolvedServerId;
        }

        const currentId = String(message?.id || '').trim();
        if (currentId && !currentId.startsWith('temp-')) {
            return currentId;
        }

        const clientRequestId = String(
            message?.clientRequestId || message?.rawMessage?.clientRequestId || ''
        ).trim();

        if (clientRequestId) {
            return resolveMessageIdFromCache(clientRequestId);
        }

        return '';
    }, [resolveMessageIdFromCache]);

    const normalizeMessagesForMutation = React.useCallback((messages) => {
        return messages.map(message => {
            const originalId = String(message?.id || '').trim();
            const canonicalId = resolveCanonicalMessageId(message);

            return {
                ...message,
                _originalId: originalId,
                _canonicalId: canonicalId,
                id: canonicalId || originalId
            };
        });
    }, [resolveCanonicalMessageId]);

    /**
     * Close message preview if deleted message is currently selected
     */
    const closeMessagePreview = message => {
        if (!selectedMessage) {
            return;
        }

        const selectedId = String(selectedMessage.id || '').trim();
        if (
            selectedId &&
            (selectedId === String(message.id || '').trim() ||
                selectedId === String(message._originalId || '').trim() ||
                selectedId === String(message._canonicalId || '').trim())
        ) {
            setSelectedMessage(null);
        }
    };

    /**
     * Filter out temp messages and return only real message IDs
     */
    const prepareMessages = messages => {
        return messages
            .map(message => String(message?._canonicalId || message?.id || '').trim())
            .filter(Boolean)
            .filter(messageId => !messageId.startsWith('temp-'));
    };

    /**
     * Perform cache update for message deletion
     */
    const performCacheUpdate = (messages) => {
        // Close preview for any deleted messages
        messages.forEach(message => {
            closeMessagePreview(message);
        });

        // Determine the correct isTrash value
        // When permanently deleting from trash, isTrash should be true
        // When in trash view (AllTrashMessagesContainer), showDeletedMessages should be true
        // But also check if the folder itself is deleted (deletedAt)
        const isTrash = selectedFolder?.deletedAt ? true : showDeletedMessages;

        // Use CacheManager to remove messages from all caches
        CacheManager.removeMessages(client, messages, {
            folderId: selectedFolder?.id,
            isTrash: isTrash,
            filterType: folderFilterType,
            isSearch: searchActive,
            searchTerm: normalizedSearchTerm,
            searchGlobal: searchGlobal,
            searchFolderFilterType: searchFolderFilterType
        });

        // Dispatch event to update memory refs in FolderContainer
        const messageIds = Array.from(new Set(
            messages.flatMap(message => [
                message?._originalId,
                message?._canonicalId,
                message?.id
            ].filter(Boolean))
        ));
        CacheManager.dispatchEvent('messagesDeleted', {
            folderId: selectedFolder?.id,
            messageIds,
            memoryKey: `${selectedFolder?.id}_${isTrash ? 'trash' : 'normal'}`
        });
    };

    /**
     * Main function to delete messages
     */
    const runThis = (messages, fromFolder) => {
        if (process.env.NODE_ENV === 'development') {
            console.log('🗑️ useDeleteMessages: Deleting', messages.length, 'messages');
        }

        const normalizedMessages = normalizeMessagesForMutation(messages);

        // Optimistically update cache IMMEDIATELY for instant UI feedback
        try {
            performCacheUpdate(normalizedMessages);
        } catch (optimisticError) {
            if (process.env.NODE_ENV === 'development') {
                console.log('Optimistic update failed, will rely on mutation update callback:', optimisticError);
            }
        }

        // Filter out temp messages before sending to server
        const realMessageIds = prepareMessages(normalizedMessages);
        
        // If all messages are temp (optimistic), just remove them from UI
        // No need to call server since they don't exist there yet
        if (realMessageIds.length === 0) {
            if (process.env.NODE_ENV === 'development') {
                    console.log('⚠️ All messages are optimistic (temp), skipping server delete');
                }
                setMessagesToSend([]);
                setSelectedMessagesMap({});
                return; // Optimistic UI update already happened above
        }

        // Now call the mutation - update callback will handle any corrections
        deleteMessages({
            variables: {
                data: {
                    messageIds: realMessageIds
                }
            },
            update: (store, { data: { deleteMessages } }) => {
                try {
                    // Run cache update again to ensure consistency after server response
                    performCacheUpdate(normalizedMessages);
                    
                    if (process.env.NODE_ENV === 'development') {
                        console.log('✅ Delete mutation completed, cache updated');
                    }
                } catch (error) {
                    if (process.env.NODE_ENV === 'development') {
                        console.error('Cache update failed during delete:', error);
                    }
                }
            },
            onError: (error) => {
                // If mutation fails, log error but don't try to revert
                // The optimistic update already happened
                if (process.env.NODE_ENV === 'development') {
                    console.error('Delete mutation failed:', error);
                }
            }
        });

        setMessagesToSend([]);
        setSelectedMessagesMap({});
        customAnalytics.delete_messages(normalizedMessages);
    };

    return [runThis, { successResponse }];
}

export default useDeleteMessagesMutation;
